// src/hooks/useVersionCheck.ts
import { generateRandomCode } from '@ringo/shared'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const MINUTE = 60000

const useVersionCheck = () => {
    const checkVersionInterval = useRef<NodeJS.Timeout | null>(null)
    const resetIgnoreInterval = useRef<NodeJS.Timeout | null>(null)
    const [isMismatch, setIsMismatch] = useState<boolean>(false)
    const [ignored, setIgnored] = useState<boolean>(false)
    const [latestVersion, setLatestVersion] = useState<string>('')
    const [activeVersion, setActiveVersion] = useState<string>('')

    const resetIgnore = useCallback(() => {
        setIgnored(false)
    }, [])

    const ignore = useCallback(() => {
        setIgnored(true)
        if (resetIgnoreInterval.current) {
            clearInterval(resetIgnoreInterval.current)
        }
        resetIgnoreInterval.current = setInterval(resetIgnore, MINUTE * 10)
    }, [resetIgnore])

    const checkVersion = useCallback(async () => {
        try {
            if (import.meta.env.VITE_ENV_NAME == 'local') return

            if (isMismatch) {
                return
            }

            const infoResponse = await fetch(`/info.json?version=${generateRandomCode()}`)
            const { version } = await infoResponse.json()
            if (!activeVersion) {
                setActiveVersion(version)
            }

            if (latestVersion && latestVersion !== version) {
                setIsMismatch(true)
                if (checkVersionInterval.current) {
                    clearInterval(checkVersionInterval.current)
                }
            }

            setLatestVersion(version)
        } catch (error) {
            console.error('Error checking version:', error)
        }
    }, [isMismatch, latestVersion])

    useEffect(() => {
        checkVersionInterval.current = setInterval(checkVersion, MINUTE)
        checkVersion()

        return () => {
            if (checkVersionInterval.current) {
                clearInterval(checkVersionInterval.current)
            }
            if (resetIgnoreInterval.current) {
                clearInterval(resetIgnoreInterval.current)
            }
        }
    }, [checkVersion])

    const showMismatchDialog = useMemo(() => {
        return !ignored && isMismatch
    }, [ignored, isMismatch])

    return { showMismatchDialog, ignore, latestVersion, activeVersion }
}

export default useVersionCheck
